import { Trans, useLingui } from '@lingui/react/macro';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import { useUpdateArticle } from '@/models/article';
import { useUpdateArticleExchange } from '@/models/articleExchange';
import { useColorOptions } from '@/models/product';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { type I18nWording, useDatabaseTranslation } from '@/services/i18n';

export const Color = ({ isReplacementProduct = false }: { isReplacementProduct?: boolean }) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const { article, view, errors } = useArticleContext();

  const { mutate: updateArticleExchange } = useUpdateArticleExchange(article.id);
  const { mutate: updateArticle } = useUpdateArticle(
    {
      articleId: article.id,
      requestId: article.requestId,
    },
    {
      optimistic: true,
    }
  );

  const color = isReplacementProduct
    ? (article.snapshot.articleExchange?.replacementColor ?? null)
    : article.color;

  const colorOptions = useColorOptions({ article, isReplacementProduct });

  const isMandatory = isReplacementProduct || view.details.color.required;

  const hasError = isReplacementProduct
    ? errors.services?.exchange?.missingReplacementProductColor
    : errors?.details?.missingColor;

  const error = hasError
    ? t({ id: 'article.form.color.error', message: 'Please select a color' })
    : undefined;

  const handleChange = (colorOption: I18nWording | null) => {
    if (isReplacementProduct) {
      updateArticleExchange({ replacementColor: colorOption });
    } else {
      updateArticle({ data: { color: colorOption } });
    }
  };

  const isDisabled = colorOptions.length === 0;

  return (
    <InputSelect
      variant="select"
      label={
        <>
          <Trans id="article.form.color.label">Color</Trans>
          {!isMandatory && !isDisabled && <OptionalLabel />}
        </>
      }
      placeholder={
        colorOptions.length > 0
          ? t({ id: 'article.form.color.placeholder', message: 'Select a color' })
          : t({ id: '_general.na', message: 'N/A' })
      }
      isDisabled={isDisabled}
      value={colorOptions.find((colorOption) => color?.en === colorOption.en) ?? null}
      options={colorOptions}
      getOptionValue={(colorOption) => colorOption.en}
      getOptionLabel={(colorOption) => _db(colorOption)}
      onChange={(colorOption) => {
        handleChange(colorOption);
      }}
      isClearable
      style={{ flex: 1 }}
      error={error}
    />
  );
};
